// Ask the gateway for a spectator pass. The pass is a short-lived
// `role: 'spectator'` token for one match plus the match-server to dial; the
// gateway only issues it to a signed-in player for a match it started.

import { exchangeSessionForToken } from '../auth/serviceToken.js';

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface SpectatorPass {
  readonly token: string;
  readonly serverUrl: string;
  readonly matchId: string;
}

export type PassResult =
  | { readonly ok: true; readonly pass: SpectatorPass }
  | { readonly ok: false; readonly reason: 'signed-out' | 'no-such-match' | 'unreachable' };

export async function requestSpectatorPass(
  gatewayHttpUrl: string,
  matchId: string,
  fetchImpl: FetchLike = fetch,
): Promise<PassResult> {
  const session = await exchangeSessionForToken(gatewayHttpUrl, fetchImpl);
  if (!session) return { ok: false, reason: 'signed-out' };
  try {
    const res = await fetchImpl(`${gatewayHttpUrl}/spectate/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${session.token}` },
      body: JSON.stringify({ matchId }),
    });
    if (res.status === 401) return { ok: false, reason: 'signed-out' };
    if (res.status === 404) return { ok: false, reason: 'no-such-match' };
    if (!res.ok) return { ok: false, reason: 'unreachable' };
    return { ok: true, pass: (await res.json()) as SpectatorPass };
  } catch {
    return { ok: false, reason: 'unreachable' };
  }
}
